import React from 'react';
import { useTranslation } from 'react-i18next';
import { COMMISSION_STATUS_OPTIONS, COMMISSION_TYPE_OPTIONS } from '../../../config/enum-options.js';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import { getCommissionStatusBadgeClass, getCommissionTypeBadgeClass, isCommissionActionable } from './commission-status-badge.js';

function CommissionsPageView({
  rows, isLoading, isSaving, error, success,
  typeFilter, setTypeFilter, statusFilter, setStatusFilter,
  isFilterOpen, toggleFilter, resetFilters, reload,
  approveCommission, cancelCommission,
}) {
  const { t } = useTranslation();

  return (
    <div>
      <FloatingAlerts error={error} success={success} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('commissions.title')}</h2>
          <p>{t('commissions.subtitle')}</p>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={toggleFilter}>
            <i className="ti ti-filter me-1"></i>{t('profiles.filters')}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={reload} disabled={isLoading}>{t('commissions.refresh')}</button>
        </div>
      </div>

      {isFilterOpen ? (
        <div className="card card-body mb-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">{t('common.type')}</label>
              <select className="form-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                {COMMISSION_TYPE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{t(opt.label)}</option>))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('common.status')}</label>
              <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                {COMMISSION_STATUS_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{t(opt.label)}</option>))}
              </select>
            </div>
            <div className="col-md-3">
              <button type="button" className="btn btn-outline-secondary" onClick={resetFilters}>{t('common.reset')}</button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
            <table className="table search-table align-middle text-nowrap mb-0">
              <thead className="header-item">
                <tr>
                  <th>{t('common.date')}</th>
                  <th>{t('commissions.partner')}</th>
                  <th>{t('common.type')}</th>
                  <th>{t('commissions.client')}</th>
                  <th>{t('commissions.depth')}</th>
                  <th>{t('commissions.lots')}</th>
                  <th>{t('commissions.amount')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && rows.length === 0 ? (
                  <tr><td colSpan="9" className="text-center text-muted">{t('commissions.noCommissionsFound')}</td></tr>
                ) : null}
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.createdAt}</td>
                    <td>
                      {row.partnerUid ? (
                        <EntityLink to={`/partners/${encodeURIComponent(row.partnerUid)}`}>{row.partnerUid.substring(0, 8)}...</EntityLink>
                      ) : '-'}
                    </td>
                    <td><span className={`badge rounded-pill fw-semibold text-uppercase ${getCommissionTypeBadgeClass(row.type)}`}>{row.type}</span></td>
                    <td>{row.clientUid ? <EntityLink to={`/profiles/${encodeURIComponent(row.clientUid)}`}>{row.clientName}</EntityLink> : row.clientName}</td>
                    <td>{row.depth}</td>
                    <td>{row.lots}</td>
                    <td>{row.amountFormatted}</td>
                    <td><span className={`badge rounded-pill fw-semibold text-uppercase ${getCommissionStatusBadgeClass(row.status)}`}>{row.status}</span></td>
                    <td>
                      {isCommissionActionable(row.status) ? (
                        <div className="d-flex gap-1">
                          <button type="button" className="btn btn-sm btn-success" onClick={() => approveCommission(row.uid)} disabled={isSaving}>{t('commissions.approve')}</button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => cancelCommission(row.uid)} disabled={isSaving}>{t('commissions.cancel')}</button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </LoadingContainer>
        </div>
      </div>
    </div>
  );
}

export default CommissionsPageView;
